<template>
  <div>
    <div v-if="loading" class="text-center mb-4">
      <v-progress-circular
        style="width: 64px; height: 64px"
        size="64"
        color="primary"
      />
    </div>
    <v-row v-if="hasNoImportedFilesError">
      <v-col cols="12">
        <Alert
          type="error"
          :dismissible="false"
          icon
        >
          {{ $t("archives.steps.ArchivesAddStep1.lblDocumentRequired") }}
        </Alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="ArchivesAddStep1-card h-100">
          <h3 class="text-center mt-0 mb-8 ArchivesAddStep1-card-title">
            {{ $t("archives.steps.ArchivesAddStep1.lblClosdFiles") }}
          </h3>
          <img
            class="d-block mx-auto my-4"
            src="@/assets/img/folder_file.svg"
            width="95"
            alt="Folder file"
          />
          <AppButton
            color="tertiary"
            dark
            small
            :disabled="loading"
            @click="openClosdFileBrowserModal"
          >
            <i class="fa fa-plus mr-2"></i>
            {{ $t("archives.steps.ArchivesAddStep1.btnClosdFiles") }}
          </AppButton>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div
          class="ArchivesAddStep1-card h-100"
          @dragover.prevent
          @dragenter.prevent="dragInProgress = true"
          @dragleave.prevent="dragInProgress = false"
          @drop.prevent="onDrop"
        >
          <h3 class="text-center mt-0 mb-8 ArchivesAddStep1-card-title">
            {{ $t("archives.steps.ArchivesAddStep1.lblExternalFiles") }}
          </h3>
          <img src="@/assets/img/uploaderIllustration.svg" width="145" alt="Uploader Illustration" />
          <p class="mt-2 mb-0 px-4 font-weight-medium">
            {{ $t("archives.steps.ArchivesAddStep1.lblExternalFilesDrop") }}
          </p>
          <p class="my-2 px-4 font-weight-semi-bold text-uppercase">
            {{ $t("archives.steps.ArchivesAddStep1.lblExternalFilesDropOr") }}
          </p>
          <input
            id="archive-file-loader"
            ref="archiveFileLoader"
            type="file"
            multiple
            class="d-none"
            @change="importFiles($event)"
          />
          <input
            id="archive-folder-loader"
            ref="archiveFolderLoader"
            type="file"
            webkitdirectory
            multiple
            style="display: none"
            @change="importFolderContent($event)"
          />
          <div class="mt-2">
            <div class="py-0">
              <AppButton
                color="tertiary"
                dark
                small
                class="my-1"
                :disabled="loading"
                @click="
                  () => {
                    this.$refs.archiveFileLoader.click();
                  }
                "
              >
                <i class="fa fa-plus mr-2"></i>
                {{ $t("archives.steps.ArchivesAddStep1.btnExternalFiles") }}
              </AppButton>
            </div>
            <div class="py-0">
              <AppButton
                color="tertiary"
                dark
                small
                class="my-1"
                :disabled="loading"
                @click="
                  () => {
                    this.$refs.archiveFolderLoader.click();
                  }
                "
              >
                <i class="fa fa-plus mr-2"></i>
                {{ $t("archives.steps.ArchivesAddStep1.btnExternalFolder") }}
              </AppButton>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <div class="mt-4">
      <AppSelect :model-value="archivingConfig"
                   :items="archivingConfigOptionsItems"
                   item-title="duration"
                   :label="$t('archives.steps.ArchivesAddStep1.lblArchiveDuration')"
                   :error-messages="archivingConfigErrorMessage"
                   :hint="$t('archives.steps.ArchivesAddStep1.lblArchiveDurationDescription')"
                   persistent-hint
                   outlined
                   color="tertiary"
                   return-object
                   @update:model-value="$emit('archivingConfig', $event)"
        >
          <template #selection="{item}">
            {{ item.title }}
            <span v-t="{ path: 'archives.steps.ArchivesAddStep1.lblYears', plural: +item.title }"
                  style="margin-left: 2px"
            />
          </template>

          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #title>
                {{ item.title }}
                <span v-t="{ path: 'archives.steps.ArchivesAddStep1.lblYears', plural: +item.title }" />
              </template>
            </v-list-item>
          </template>
        </AppSelect>
    </div>

    <div v-if="importedFiles.length > 0" class="uploaded-file-list mt-4">
      <ArchivesUploadedFileListItem v-for="(file, i) in importedFiles"
                                    :key="`file-${i}`"
                                    class="uploaded-file"
                                    :file="file"
                                    @delete-file="deleteFile"
      />
    </div>

    <ArchivesClosdFileBrowserDialog v-if="closdFileBrowserDialogIsOpened"
                                    @selectedFiles="importClosdFiles"
                                    @close="closdFileBrowserDialogIsOpened = false"
    />
  </div>
</template>

<script>
import { getFilesFromDataTransferItems } from 'datatransfer-files-promise'
import { createNamespacedHelpers } from 'vuex'

import { formatImportedFile } from '@/common/utils/archives'

import ArchivesClosdFileBrowserDialog from './ArchivesClosdFileBrowserDialog.vue'
import ArchivesUploadedFileListItem from './ArchivesUploadedFileListItem.vue'
import Alert from '../../common/alerts/Alert.vue'
import AppSelect from '../../common/AppSelect.vue'
import AppButton from '../../common/buttons/AppButton.vue'

const { mapGetters } = createNamespacedHelpers('archives')

export default {
  name: 'ArchivesAddStep1',
  components: {
    Alert,
    AppButton,
    AppSelect,
    ArchivesClosdFileBrowserDialog,
    ArchivesUploadedFileListItem,
  },
  props: {
    validator: {
      type: Object,
      required: true,
    },
    importedFiles: {
      type: Array,
      required: true,
    },
    archivingConfig: {
      required: true,
      validator: (value) => {
        return value === null || typeof value === 'object'
      },
    },
  },
  data () {
    return {
      dragInProgress: false,
      loading: false,
      closdFileBrowserDialogIsOpened: false,
    }
  },
  computed: {
    ...mapGetters(['sections']),
    archivingConfigState () {
      if (!this.validator.archivingConfig.$anyDirty) {
        return true
      } else if (this.validator.archivingConfig.$anyError) {
        return false
      }
      return true
    },
    archivingConfigErrorMessage () {
      return this.archivingConfigState
        ? []
        : [this.$t('archives.steps.ArchivesAddStep1.lblArchiveDurationRequired')]
    },
    archivingConfigOptionsItems () {
      return (this.sections ?? []).map(archivingConfig => ({
        id: archivingConfig.id,
        duration: archivingConfig.duration,
      }))
    },
    hasNoImportedFilesError () {
      return this.validator.$dirty && this.validator.importedFiles.$error
    },
  },
  methods: {
    importFiles () {
      this.loading = true
      const queue = [...this.importedFiles]
      for (const f of this.$refs.archiveFileLoader.files) {
        queue.push(formatImportedFile(f, this.archivingConfig.id))
      }
      this.$emit('updateUploadedFiles', queue)
      this.loading = false
      this.$refs.archiveFileLoader.value = ''
    },
    importFolderContent () {
      this.loading = true
      for (const f of this.$refs.archiveFolderLoader.files) {
        this.importedFiles.push(formatImportedFile(f, this.archivingConfig.id))
      }
      this.loading = false
      this.$refs.archiveFolderLoader.value = ''
    },
    importClosdFiles (e) {
      this.loading = true
      for (const f of e) {
        this.importedFiles.push((formatImportedFile(f, this.archivingConfig.id)))
      }
      this.loading = false
    },
    async onDrop (e) {
      let files = e.dataTransfer ? e.dataTransfer.items : 'null'
      if (files) {
        this.loading = true
        const droppedFiles = await getFilesFromDataTransferItems(files)
        files = [...this.importedFiles, ...droppedFiles.map((file) => formatImportedFile(file, this.archivingConfig.id))]
        this.$emit('updateUploadedFiles', files)
        this.loading = false
      }
    },
    deleteFile (e) {
      try {
        const fileToDelete = this.importedFiles.findIndex(f => f === e)
        this.importedFiles.splice(fileToDelete, 1)
      } catch (e) {
        console.error(e)
      }
    },
    openClosdFileBrowserModal () {
      this.closdFileBrowserDialogIsOpened = true
    },
  },
}
</script>

<style scoped lang="scss">
.ArchivesAddStep1-card {
  border: 2px dashed #d8d8d8;
  border-radius: 0.35rem;
  text-align: center;
  padding-top: 1rem;
  padding-bottom: 1.5rem;
}

.ArchivesAddStep1-card-title {
  font-size: 17px;
  font-weight: normal;
}

.uploaded-file-list {
  & > .uploaded-file {
    padding-block: 8px;
    &:not(:last-child) {
      border-bottom: 1px solid rgb(var(--v-theme-grey-100));
    }
  }
}
</style>
